// Caption events (decision 0063: captions for important sounds). Every sound the game flags important sends one
// caption event when it plays, whether or not it is heard (a muted bus, a culled voice, a context not yet unlocked:
// the caption is for the player who can't hear it). The event carries the caption's string id and, for a positional
// sound, which side of the listener it came from; the game formats it ("[growl, left]") and draws it.

/** A point or direction `[x, y, z]`. */
export type Vec3 = readonly [number, number, number];

/** Where a sound came from, relative to the listener. */
export type CaptionDirection = "left" | "right" | "behind" | "ahead";

/** One caption. */
export interface CaptionEvent {
  /** The cue that played. */
  cue: string;
  /** The caption's string id (the game's table). */
  caption: string;
  /** null for a sound on the listener (or too close to have a side). */
  direction: CaptionDirection | null;
  /** Context seconds (or the engine's own clock before a context exists). */
  at: number;
}

/** The side a sound at `pos` is on for a listener at `listener` facing `forward` (horizontal plane). Within `near` m
 *  it has no side. Ahead within ±35°, behind past ±145°, else left or right. */
export function directionOf(pos: Vec3, listener: Vec3, forward: Vec3, near = 1.5): CaptionDirection | null {
  const dx = pos[0] - listener[0];
  const dz = pos[2] - listener[2];
  if (Math.hypot(dx, dz) < near) return null;
  const fl = Math.hypot(forward[0], forward[2]) || 1;
  const fx = forward[0] / fl;
  const fz = forward[2] / fl;
  // signed angle from forward to the source, positive to the listener's right (three.js: right = forward × up)
  const dot = fx * dx + fz * dz;
  const cross = fx * dz - fz * dx; // > 0: the source is to the right of forward (x right, z towards the viewer)
  const ang = Math.atan2(cross, dot);
  const a = Math.abs(ang);
  if (a <= (35 * Math.PI) / 180) return "ahead";
  if (a >= (145 * Math.PI) / 180) return "behind";
  return ang > 0 ? "right" : "left";
}
